import { DestroyRef, inject, Injectable } from '@angular/core';
import type { AsyncValidatorFn, ValidatorFn } from '@angular/forms';
import { FormControl, FormGroup } from '@angular/forms';
import { QuerifyService } from '../../shared/services/querify/querify.service';
import type { PalantirModalRef } from '../modal/modal-ref.service';
import { PalantirModalService } from '../modal/modal.service';
import { PalantirFilterModalComponent } from './filter-modal.component';
import { PalantirFilterModalInstance } from './filter-modal.instance';
import {
  PalantirFilterClearFiltersOnDestroyBehavior,
  PalantirFilterInputType,
  PalantirFilterInvalidBehavior,
  PalantirFilterSaveStrategy,
} from './filter-modal.type';
import type {
  PalantirFilterModalOptions,
  PalantirFilterInput,
  PalantirFilterModalDataToFormGroup,
  PalantirFilterModalDataToFormGroupArgument,
  PalantirFilterSelectableValue,
  PalantirFilterValidatorOptions,
} from './filter-modal.type';
import { createSimpleHash } from './filter-modal.utils';
import { filterModalLogger } from './logger.instance';

const logger = filterModalLogger.extend('service');

@Injectable({
  providedIn: 'root',
})
export class PalantirFilterModalService {
  /**
   * Comprimento máximo do ID gerado automaticamente para o modal de filtro.
   * - O ID é gerado a partir dos filtros e é limitado a esse comprimento.
   * - Ids customizados não são afetados por esse limite.
   */
  public static readonly GENERATED_MODAL_ID_LENGTH = 3;

  private modalsRefs: Record<string, PalantirFilterModalInstance<any>> = {};

  public constructor(
    private readonly palantirModalService: PalantirModalService,
    public readonly querifyService: QuerifyService,
  ) {}

  /**
   * @param options - Os filtros e configurações do diálogo
   * @param destroyRef - Referência de destruição, caso não seja passada, será injetada automaticamente e a função precisa ser chamada dentro de um contexto de injeção
   * @throws - Se o destroyRef não for passado e a função não for chamada dentro de um contexto de injeção
   */
  public filterModalFactory<T extends Record<string, PalantirFilterInput>>(
    options: PalantirFilterModalOptions<T>,
    destroyRef?: DestroyRef,
  ): PalantirFilterModalInstance<T> {
    try {
      // eslint-disable-next-line no-param-reassign
      destroyRef ??= inject(DestroyRef);
    } catch {
      throw new Error(
        'The destroyRef parameter must be passed or the function must be called within an injection context',
      );
    }

    const id = options.customId ?? this.generateIdFromFilters(options.filters);

    logger.debug(`Factory creating modal with id: ${id}`, { customId: options.customId });

    if (this.modalsRefs[id] && !options.overrideModalIdOnConflict) {
      throw new Error(
        `A filter modal with the id "${id}" already exists. Please use a different id or leave it empty to generate a one.`,
      );
    }

    if (this.modalsRefs[id] && options.overrideModalIdOnConflict) {
      logger.warn(
        `A filter modal with the id "${id}" already exists. It will be overridden with the new instance.`,
      );
      this.modalsRefs[id].destroy();
    }

    this.modalsRefs[id] = new PalantirFilterModalInstance({
      id,
      metadata: {
        ...options,
        // Define os valores padrão para as opções de comportamento
        clearFiltersOnDestroyBehavior:
          options.clearFiltersOnDestroyBehavior ??
          PalantirFilterClearFiltersOnDestroyBehavior.CLEAR,
        invalidFormsBehavior: options.invalidFormsBehavior ?? PalantirFilterInvalidBehavior.IGNORE,
        saveFiltersStrategy: options.saveFiltersStrategy ?? PalantirFilterSaveStrategy.QUERY_STRING,
      },
      formGroup: this.mapFilterDataToFormGroup(options.filters),
      destroyRef,
      filterModalService: this,
    });

    logger.info(`Filter modal with id "${id}" created successfully.`);

    return this.modalsRefs[id];
  }

  /**
   * Obtém uma instância de diálogo de filtro modal com base no ID fornecido.
   *
   * @param id - O ID do diálogo a ser obtido
   * @returns Uma instância do diálogo modal correspondente ao ID fornecido
   */
  public getInstance<T extends Record<string, PalantirFilterInput> = any>(
    id: string,
  ): PalantirFilterModalInstance<T> | null {
    return this.modalsRefs[id] ?? null;
  }

  /**
   * - **ATENÇÃO:** Essa função normalmente é chamada internamente pelo `PalantirFilterModalInstance#open()`.
   *
   * @param id - O ID do diálogo a ser aberto
   * @returns Uma referência ao diálogo modal aberto
   * @internal
   */
  public openModal(id: string): PalantirModalRef {
    logger.log(`Opening modal with id: ${id}`);
    return this.palantirModalService.openModal(PalantirFilterModalComponent, {
      data: { id },
      size: 'small',
    });
  }

  /**
   * Remove um diálogo de filtro modal com base no ID fornecido.
   * - **ATENÇÃO:** Essa função não fecha o nem limpa o modal, apenas remove a referência do diálogo. Considere usar o `PalantirFilterModalInstance#destroy()`.
   *
   * @param id - O ID do diálogo a ser removido
   */
  public removeDialog(id: string): void {
    logger.debug(`Removing modal reference for id: ${id}`);
    Reflect.deleteProperty(this.modalsRefs, id);
  }

  /**
   * @internal
   * @param data - Os dados dos filtros a serem mapeados
   * @returns Um FormGroup contendo os controles de formulário correspondentes aos filtros
   */
  private mapFilterDataToFormGroup<T extends Record<string, PalantirFilterInput>>(
    data: T,
  ): PalantirFilterModalDataToFormGroup<T> {
    const dataWithForm = {} as PalantirFilterModalDataToFormGroupArgument<T>;

    for (const [key, value] of Object.entries(data)) {
      switch (value.type) {
        case PalantirFilterInputType.TEXT:
          Reflect.set(
            dataWithForm,
            key,
            new FormControl<string | null>(
              value.value ?? null,
              ...this.unwrapValidators(value.validators),
            ),
          );
          break;
        case PalantirFilterInputType.NUMBER:
          Reflect.set(
            dataWithForm,
            key,
            new FormControl<number | null>(
              value.value ?? null,
              ...this.unwrapValidators(value.validators),
            ),
          );
          break;
        case PalantirFilterInputType.DATE:
          Reflect.set(
            dataWithForm,
            key,
            new FormControl<Date | null>(
              value.value ?? null,
              ...this.unwrapValidators(value.validators),
            ),
          );
          break;
        case PalantirFilterInputType.DATE_RANGE:
          Reflect.set(
            dataWithForm,
            key,
            new FormGroup<{
              from: FormControl<Date | null>;
              to: FormControl<Date | null>;
            }>({
              from: new FormControl<Date | null>(
                value.from.value ?? null,
                ...this.unwrapValidators(value.from.validators),
              ),
              to: new FormControl<Date | null>(
                value.to.value ?? null,
                ...this.unwrapValidators(value.to.validators),
              ),
            }),
          );
          break;
        case PalantirFilterInputType.SEARCH:
        case PalantirFilterInputType.SELECT:
          Reflect.set(
            dataWithForm,
            key,
            new FormControl<PalantirFilterSelectableValue | null>(
              value.value ?? null,
              ...this.unwrapValidators(value.validators),
            ),
          );
          break;
        case PalantirFilterInputType.MULTI_SEARCH:
        case PalantirFilterInputType.MULTI_SELECT:
          Reflect.set(
            dataWithForm,
            key,
            new FormControl<PalantirFilterSelectableValue[]>(
              value.value ?? [],
              ...this.unwrapValidators(value.validators),
            ),
          );
          break;
        case PalantirFilterInputType.CUSTOM:
          Reflect.set(dataWithForm, key, value.formFactory());
          break;
        default:
          throw new TypeError(`Invalid filter input type: ${(value as { type: string }).type}`);
      }
    }

    return new FormGroup(dataWithForm);
  }

  /**
   * @internal
   * @param filters - Os filtros a serem usados para gerar o ID do modal
   * @returns Um ID único gerado a partir dos filtros, limitado a `PalantirFilterModalService.GENERATED_MODAL_ID_LENGTH` caracteres ou 5 caracteres no máximo
   */
  private generateIdFromFilters(filters: Record<string, PalantirFilterInput>): string {
    const keys = Object.keys(filters)
      .sort((a, b) => a.localeCompare(b))
      .join('-');

    return createSimpleHash(keys).slice(0, PalantirFilterModalService.GENERATED_MODAL_ID_LENGTH);
  }

  /**
   * @internal
   * @param validators - Os validadores a serem desestruturados
   * @returns
   */
  private unwrapValidators(
    validators: PalantirFilterValidatorOptions | undefined,
  ): [ValidatorFn[], AsyncValidatorFn[]] {
    if (!validators) {
      return [[], []];
    }

    if (Array.isArray(validators)) {
      return [validators, []];
    }

    return [validators.validators ?? [], validators.asyncValidators ?? []];
  }
}
